/**
 * Generates a color palette from a given base color by progressively lightening and darkening it.
 *
 * The palette is constructed by applying the specified lighten and darken factors from the configuration.
 * Each step is labeled sequentially (e.g., `p0`, `p5`, `p10`, etc.), with the base color positioned in the middle.
 *
 * @function generatePalette
 * @param {string} base - The base color in any valid CSS color format (e.g., `#3498db`, `rgb(52, 152, 219)`).
 * @param {Object} [config=PaletteConfig] - Optional configuration for palette generation.
 * @param {number[]} config.LIGHTEN_FACTORS - Factors used to progressively lighten the base color.
 * @param {number[]} config.DARKEN_FACTORS - Factors used to progressively darken the base color.
 * @param {number} config.STEP - Numeric increment used to label the palette keys (e.g., `p0`, `p5`, `p10`).
 *
 * @returns {Record<string, string>} An object representing the generated color palette.
 * The keys are step labels (e.g., `p0`, `p5`, `p10`), and the values are hex color strings.
 *
 * @example
 * generatePalette("#3498db");
 * // {
 * //   p0: "#d4e9fa",
 * //   p5: "#c2def8",
 * //   p10: "#b0d4f6",
 * //   ...,
 * //   p50: "#3498db", // base color
 * //   p55: "#2f89c5",
 * //   p60: "#297aac",
 * //   ...
 * // }
 */

import { colord } from 'colord';

const PaletteConfig = {
    LIGHTEN_FACTORS: [0.55, 0.5, 0.45, 0.4, 0.35, 0.3, 0.25, 0.2, 0.15, 0.1],
    DARKEN_FACTORS: [0.1, 0.15, 0.2, 0.25, 0.3, 0.35, 0.4, 0.45, 0.5, 0.55],
    STEP: 5,
};

export function generatePalette(
    base: string,
    config = PaletteConfig
): Record<string, string> {
    const c = colord(base);
    const palette: Record<string, string> = {};
    config.LIGHTEN_FACTORS.forEach((factor, i) => {
        const key = `p${i * config.STEP}`;
        palette[key] = c.lighten(factor).toHex();
    });
    palette[`p${config.LIGHTEN_FACTORS.length * config.STEP}`] = base;
    config.DARKEN_FACTORS.forEach((factor, i) => {
        const key = `p${(config.LIGHTEN_FACTORS.length + 1) * config.STEP + i * config.STEP}`;
        palette[key] = c.darken(factor).toHex();
    });

    return palette;
}
